import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "sonner";
import { ChevronDown, Download, ListTree, MoreVertical, Undo2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import ImportWizard from "@/components/imports/ImportWizard";
import { useImportIssuesDownload } from "@/hooks/useImportIssuesDownload";
import type { ImportEntityType, ImportRunSummary } from "@/model/interface/DataImport";
import { restApi, useGetImportRunsQuery, useUndoImportRunMutation } from "@/store/rest_api";
import { apiErrorMessage } from "@/util/apiError";
import { formatDateTime } from "@/util/date";
import {
  importRecordsPath,
  importRecordTags,
  importRunKindLabel,
  importStatusPills,
  isImportRunActive,
  type ImportRecordAction,
} from "@/util/importRuns";

interface ImportChoice {
  label: string;
  entityType: ImportEntityType;
  contactType?: "LEAD" | "CONTACT";
}

const IMPORT_CHOICES: ImportChoice[] = [
  { label: "Leads", entityType: "CONTACT", contactType: "LEAD" },
  { label: "Contacts", entityType: "CONTACT", contactType: "CONTACT" },
  { label: "Properties and owners", entityType: "PROPERTY" },
  { label: "Do Not Contact numbers", entityType: "DNC" },
  { label: "Tasks", entityType: "TASK" },
  { label: "Notes and call or text history", entityType: "NOTE" },
];

const PAGE_SIZE = 20;
const POLL_INTERVAL_MS = 3000;
const QUEUED_POLL_INTERVAL_MS = 60000;

export default function ImportHistoryPage() {
  const dispatch = useDispatch();
  const [page, setPage] = useState(0);
  const [pollingInterval, setPollingInterval] = useState(0);
  const [wizardChoice, setWizardChoice] = useState<ImportChoice | null>(null);
  const [mappingRun, setMappingRun] = useState<ImportRunSummary | null>(null);
  const [undoRun, setUndoRun] = useState<ImportRunSummary | null>(null);
  const hadActiveRun = useRef(false);
  const { data, isLoading } = useGetImportRunsQuery({ page, size: PAGE_SIZE }, { pollingInterval });
  const [undoImportRun, { isLoading: isUndoing }] = useUndoImportRunMutation();
  const { downloadIssues } = useImportIssuesDownload();

  const runs = data?.items ?? [];
  const hasActiveRun = runs.some(isImportRunActive);
  const hasRunningRun = runs.some((run) => run.status === "RUNNING");

  async function confirmUndo() {
    if (!undoRun) {
      return;
    }
    try {
      await undoImportRun(undoRun.id).unwrap();
      toast.success("Undo started. You get a notification when it is done.");
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "The import could not be undone"));
    } finally {
      setUndoRun(null);
    }
  }

  function renderCount(run: ImportRunSummary, value: number, action?: ImportRecordAction) {
    const path = action && value > 0 && run.status === "COMPLETED" ? importRecordsPath(run, action) : null;
    if (path) {
      return (
        <Link to={path} className="font-medium underline-offset-4 hover:underline">
          {value.toLocaleString()}
        </Link>
      );
    }
    return <span className={value === 0 ? "text-muted-foreground" : undefined}>{value.toLocaleString()}</span>;
  }

  useEffect(() => {
    setPollingInterval(hasRunningRun ? POLL_INTERVAL_MS : hasActiveRun ? QUEUED_POLL_INTERVAL_MS : 0);
    if (hadActiveRun.current && !hasActiveRun) {
      dispatch(restApi.util.invalidateTags([...importRecordTags]));
    }
    hadActiveRun.current = hasActiveRun;
  }, [hasActiveRun, hasRunningRun, dispatch]);

  return (
    <div className="space-y-6 p-4 text-foreground">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Imports</h1>
          <p className="text-muted-foreground">
            Every file imported into this workspace. An import can be undone for 14 days after it finishes.
          </p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button>
              <Upload className="mr-2 h-4 w-4" />
              New import
              <ChevronDown className="ml-2 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {IMPORT_CHOICES.map((choice) => (
              <DropdownMenuItem key={choice.label} onClick={() => setWizardChoice(choice)}>
                {choice.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {isLoading && <Skeleton className="h-64 w-full" />}

      {!isLoading && (
        <div className="overflow-x-auto rounded-[var(--radius)] border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>File</TableHead>
                <TableHead>Imported by</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Added</TableHead>
                <TableHead className="text-right">Updated</TableHead>
                <TableHead className="text-right">Duplicates</TableHead>
                <TableHead className="text-right">Skipped</TableHead>
                <TableHead className="text-right">Errors</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.length > 0 ? (
                runs.map((run) => {
                  const pill = importStatusPills[run.status];
                  const active = isImportRunActive(run);
                  return (
                    <TableRow key={run.id}>
                      <TableCell className="max-w-64">
                        <div className="truncate font-medium" title={run.fileName ?? undefined}>
                          {run.fileName ?? "Import"}
                        </div>
                        <div className="text-xs text-muted-foreground">{importRunKindLabel(run)}</div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{run.createdByName ?? "System"}</TableCell>
                      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                        {formatDateTime(run.createdAt)}
                      </TableCell>
                      <TableCell>
                        <span className={`${pill.className} tabular-nums`}>
                          {run.status === "RUNNING" && run.totalRows > 0
                            ? `${pill.label} ${Math.min(100, Math.round((run.processedRows / run.totalRows) * 100))}%`
                            : pill.label}
                        </span>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{renderCount(run, run.addedCount, "CREATED")}</TableCell>
                      <TableCell className="text-right tabular-nums">{renderCount(run, run.updatedCount, "UPDATED")}</TableCell>
                      <TableCell className="text-right tabular-nums">{renderCount(run, run.duplicateCount)}</TableCell>
                      <TableCell className="text-right tabular-nums">{renderCount(run, run.skippedCount)}</TableCell>
                      <TableCell className="text-right tabular-nums">{renderCount(run, run.errorCount)}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" title="Options">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setMappingRun(run)} disabled={run.mappedColumns.length === 0}>
                              <ListTree className="mr-2 h-4 w-4" />
                              View mapping
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => downloadIssues(run)} disabled={active}>
                              <Download className="mr-2 h-4 w-4" />
                              Download row issues
                            </DropdownMenuItem>
                            {run.canUndo && (
                              <DropdownMenuItem onClick={() => setUndoRun(run)} className="text-destructive">
                                <Undo2 className="mr-2 h-4 w-4" />
                                Undo import
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={10} className="py-10 text-center text-muted-foreground">
                    No imports yet. Use New import to bring in leads, contacts, properties, Do Not Contact numbers,
                    tasks or notes.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {data && data.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Page {data.page + 1} of {data.totalPages}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={data.page === 0} onClick={() => setPage((current) => current - 1)}>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={data.last} onClick={() => setPage((current) => current + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}

      <Dialog open={mappingRun !== null} onOpenChange={(open) => (!open ? setMappingRun(null) : null)}>
        <DialogContent className="max-w-lg text-foreground">
          <DialogHeader>
            <DialogTitle>Mapping used for {mappingRun?.fileName ?? "this import"}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-auto rounded-[var(--radius)] border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Column in the file</TableHead>
                  <TableHead>Imported as</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mappingRun?.mappedColumns.map((mappedColumn) => (
                  <TableRow key={mappedColumn.column}>
                    <TableCell className="font-medium">{mappedColumn.column}</TableCell>
                    <TableCell className="text-muted-foreground">{mappedColumn.field}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={undoRun !== null} onOpenChange={(open) => (!open ? setUndoRun(null) : null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Undo this import?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the records {undoRun?.fileName ?? "the import"} added, and the phones, emails, tags and list
              memberships it added to existing records. Deals it created are deleted. Leads and contacts with calls or
              messages since the import are archived instead of deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void confirmUndo()}
              disabled={isUndoing}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Undo import
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {wizardChoice && (
        <ImportWizard
          open
          onClose={() => setWizardChoice(null)}
          entityType={wizardChoice.entityType}
          contactType={wizardChoice.contactType}
        />
      )}
    </div>
  );
}
